<script lang="ts">
  import { clickOutside } from "$app/actions"
  /**
  <Tiptap
    id={`comment-${post.id}`}
    placeholder="Share your thoughts …"
    content={newComment}
    saving={savingComment}
    onsave={saveComment}
    allowFileUploads={true}
    allowEmojis={true}
    allowFileUploads={true}
    showSaveArrow={true}
    allowGiphy={true}
    buttons={[
      ["bold", "italic", "underline", "strike"],
      ["bulletList", "orderedList", "blockquote"],
      ["link", "unlink", "image"]
    ]}
    onupdate={({html}) => console.log(html)}
  />
  */
  import { onMount, onDestroy, type Snippet } from "svelte"
  import { Wave } from "svelte-loading-spinners"
  import { Editor } from "@tiptap/core"
  import StarterKit from "@tiptap/starter-kit"
  import ExtensionUnderline from "@tiptap/extension-underline"
  import Placeholder from "@tiptap/extension-placeholder"
  import { Color } from "@tiptap/extension-color"
  import TextStyle from "@tiptap/extension-text-style"
  import ExtensionLink from "@tiptap/extension-link"
  import Image from "$app/components/Tiptap/image"
  import StyleButton from "$app/components/Tiptap/StyleButton.svelte"
  import { SendHorizontal, Paperclip, Smile, Loader } from "lucide-svelte"
  import Giphy from "$app/components/Tiptap/Giphy.svelte"
  import Emoji from "$app/components/Tiptap/Emoji.svelte"
  import TiptapFile from "$app/components/Tiptap/TiptapFile.svelte"
  import {
    imageSizes,
    SIZE_W,
    SIZE_H
  } from "$app/components/Tiptap/TiptapFile.svelte"
  import { appStore } from "$app/store.svelte"
  import { toast, xhr } from "$app/utils"
  import { LoaderCircle } from "lucide-svelte"
  import { setOpenGraph } from "$app/components/Tiptap/opengraph"

  let props = $props()
  let {
    id = "new",
    content = "",
    saving = false,
    placeholder = "Write something …",
    buttons,
    allowEmojis = false,
    allowGiphy = false,
    showSaveArrow = false,
    allowFileUploads = false,
    files = [],
    uploadingFile = false,
    bottom,
    onupdate = () => {},
    onsave,
    onready,
    onblur,
    onfileupload,
    ondeletefile,
    oninsertimage
  }: {
    id?: string
    content?: string
    saving?: boolean
    placeholder?: string
    buttons?: ButtonType[][]
    allowEmojis?: boolean
    allowGiphy?: boolean
    showSaveArrow?: boolean
    allowFileUploads?: boolean
    files?: ActiveStorageType[]
    uploadingFile?: boolean
    bottom?: Snippet
    onupdate?: ({ html }: { html: string }) => void
    onsave?: ({ html }: { html: string }) => void
    onready?: ({ editor }: { editor: Editor }) => void
    onblur?: ({ html }: { html: string }) => void
    onfileupload?: (file: File) => void
    ondeletefile?: (file: ActiveStorageType) => void
    oninsertimage?: (data: InsertImgType) => void
  } = props

  let element = $state<HTMLElement | undefined>()
  let editor = $state<Editor | undefined>()
  let canSave = $state(true)
  let fileUploadElement = $state<HTMLInputElement | undefined>()
  let fileList = $state<FileList | null>(null)
  let processingLink: "" | "working" = $state("")

  let prevContent = JSON.parse(JSON.stringify(content))
  let evt: any = null

  $effect(() => {
    if (content != prevContent) {
      editor.commands.setContent(content)
      prevContent = JSON.parse(JSON.stringify(content))
    }
  })

  let mounted = false
  let editorUpdated = new Date().getTime()
  onMount(() => {
    editor = new Editor({
      element: element,
      editorProps: {
        attributes: {
          class: `${props.class || ""} overflow-y-scroll`
        }
      },
      extensions: [
        StarterKit,
        ExtensionUnderline,
        TextStyle,
        Color,
        ExtensionLink.configure({
          openOnClick: false,
          autolink: true
        }),
        Placeholder.configure({
          // Use a placeholder:
          placeholder: placeholder
        }),
        Image.configure({
          allowBase64: true,
          inline: true
        })
      ],
      content: content,
      onTransaction: () => {
        // force re-render so `editor.isActive` works as expected
        editor = editor
      },
      onCreate({ editor }) {
        // setCanSave(editor)
      },
      onFocus({ editor }) {
        // setCanSave(editor)
      },
      onUpdate({ editor }) {
        // setCanSave(editor)
        evt = editor
        onupdate({ html: editor.getHTML() })
        editorUpdated = new Date().getTime()
      },
      onBlur({ editor, event }) {
        // onblur
      },
      // The editor is being pasted into.
      async onPaste(e) {
        let text = e?.clipboardData?.getData("text/plain")
        if (!text) return
        if (!editor) return

        processingLink = "working"
        try {
          await setOpenGraph(editor, text)
        } catch (e) {
          console.error(e)
        } finally {
          processingLink = ""
        }
      }
    })

    if (onready) onready({ editor })

    mounted = true
  })

  onDestroy(() => {
    editor?.destroy()
  })

  function save() {
    canSave =
      (editor &&
        editor.getHTML().length > 0 &&
        editor.getHTML() !== "<p></p>") ||
      files?.length > 0

    if (canSave) {
      onsave({ html: editor.getHTML() })
    }
  }

  function insertGiphy({
    image,
    width,
    height
  }: {
    image: giphyType
    width: number
    height: number
  }): void {
    let style = ""
    if (width > height) {
      style = `width:200px`
    } else {
      style = `height:200px`
    }
    editor.commands.setImage({ src: image.images.original.url, style: style })
  }

  function validateFileSize(file: File) {
    const fileSize = file.size
    const fileMb = fileSize / 1024 ** 2
    if (fileMb >= 3) {
      alert(
        "The file is too large to upload. Please select a file less than 3MB"
      )
      return false
    }

    return true
  }

  function fileUpload(file: File): void {
    if (!validateFileSize(file)) {
      throw new Error("Invalid file size")
    }

    onfileupload(file)
  }

  function handleUpload(file: File): void | boolean {
    if (!validateFileSize(file)) {
      return false
    }

    editor.setEditable(false)

    onfileupload(file)
  }

  type InsertImgType = {
    file: ActiveStorageType
    width: number
    height: number
  }

  function insertImage(data: InsertImgType): void {
    const { file, width, height } = data

    const url = file?.blob?.url || file?.url

    editor.commands.setImage({
      src: url,
      alt: file.filename,
      style: `width:${width}px;height:${height}px`
    })
  }

  async function dropFile(e: DragEvent): Promise<void> {
    if (e.dataTransfer?.items) {
      let items = [...e.dataTransfer.items]
      if (!/^image\/(png|jpeg|gif|webp)/.test(items[0].type)) {
        alert("You can only drag and drop images")
        return
      }

      const file = items[0].getAsFile()
      await fileUpload(file)
      if (items.length > 1) alert("We only allow one image")
    }
  }

  let imgModalShow = false
  let imgUrl = ""
  let imgSize = imageSizes[1].size

  async function insertImageCallback(): Promise<void> {
    const img = document.createElement("img")
    img.src = imgUrl
    img.style.display = "none"
    document.body.appendChild(img)
    await new Promise((res: Function) => {
      if (img.complete) return res()
      img.onload = () => {
        res()
      }
    })

    const w = img.naturalWidth
    const h = img.naturalHeight
    const selectedSizeRange = w >= h ? SIZE_W : SIZE_H

    let width: number
    let height: number

    const selDim = selectedSizeRange[imgSize]
    // Check if the size is H or W
    if (selectedSizeRange["xl"] === SIZE_H["xl"]) {
      ;[width, height] = [(w / h) * selDim, selDim]
    } else {
      ;[width, height] = [selDim, (h / w) * selDim]
    }
    editor.commands.setImage({
      src: imgUrl,
      alt: "Inserted image",
      style: `width:${width}px;height:${height}px`
    })

    imgModalShow = false

    img.remove()
  }

  let inputFile: HTMLInputElement | null = null

  let uploadingImage = $state(false)
  async function handleFileChange(e: Event): Promise<void> {
    uploadingImage = true
    const target = e.target as HTMLInputElement
    if (target.files.length < 1) return

    const file = target.files[0]
    const buffer = await file.arrayBuffer()
    const blob = new Blob([buffer], { type: file.type })
    const name = file.name.replace(/^.*[\/]/, "")

    let formData = new FormData()
    formData.append("attachment[file]", blob)
    formData.append("attachment[name]", name)
    formData.append("attachment[attachable_type]", "Plan")
    formData.append("attachment[attachable_id]", appStore.plan.id.toString())

    try {
      const resp = await xhr.post(
        `/api/v1/plans/${appStore.plan.id}/attachments`,
        formData
      )
      const imgUrl = `/api/v1/plans/${appStore.plan.id}/attachments/${resp.data.id}?uuid=${resp.data.uuid}`

      editor.commands.setImage({ src: imgUrl })
    } catch (err) {
      toast("There was an error uploading your image", "error")
      return
    } finally {
      uploadingImage = false
    }
  }
  function uploadImage(): void {
    inputFile?.click()
  }
</script>

<input
  type="file"
  accept=".png,.jpg"
  bind:this={inputFile}
  class="hidden"
  onchange={handleFileChange}
/>

<div
  ondrop={(e) => {
    e.preventDefault()
    dropFile(e)
  }}
  role="region"
>
  <div
    id={`tiptap-editor-${id}`}
    use:clickOutside={() => {
      if (onblur) onblur({ html: editor.getHTML() })
    }}
    class="bg-core border-gray-200 border-2 rounded-lg"
  >
    <!-- Style buttons -->
    <div class="w-full">
      {#if editor}
        <div class="w-full flex items-center gap-0.5 px-1 py-0.5 bg-gray-100">
          {#each buttons as line, index (index)}
            {#each line as button, idx (idx)}
              <StyleButton {button} {editor} />
            {/each}
            <div class="bg-gray-300 w-0.5 h-5 mx-1"></div>
          {/each}

          <StyleButton button="undo" {editor} />
          <StyleButton button="redo" {editor} />
        </div>
      {/if}
      <div class="relative">
        <div
          bind:this={element}
          oninsertimage={(e) => uploadImage(e)}
          class="p-4 outline-transparent focus:outline-0 !text-gray-800"
        ></div>
      </div>
    </div>

    <div class="flex justify-between px-2 py-1 border-t border-gray-200">
      <!-- Footer -->
      <div class="flex items-center space-x-5 mt-1">
        {#if allowFileUploads}
          <div class="flow-root group relative">
            <input
              class="hidden"
              id="file-to-upload"
              type="file"
              accept=".png,.jpg"
              bind:files={fileList}
              bind:this={fileUploadElement}
              onchange={() => {
                if (fileList && fileList[0]) fileUpload(fileList[0])
              }}
            />
            <button
              type="button"
              data-name="upload-file"
              onclick={() => fileUploadElement.click()}
              class="-m-2 inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-400 hover:text-gray-500"
            >
              <Paperclip class="w-6 h-6 !fill-gray-400 hover:!text-gray-500" />
              <span class="sr-only">Attach an image</span>
            </button>
            <span
              class="group-hover:opacity-100 transition-opacity bg-gray-800 px-1 text-sm text-gray-100 rounded-md absolute left-1/2 -translate-y-10 opacity-0 m-4 mx-auto whitespace-nowrap"
            >
              Upload image
            </span>
          </div>
        {/if}

        <div class="flow-root group relative">
          {#if true && allowEmojis}
            <Emoji
              {id}
              onemoji={(detail) =>
                editor.commands.insertContent(detail.unicode)}
            />
            <span
              class="group-hover:opacity-100 transition-opacity bg-gray-800 px-1 text-sm text-gray-100 rounded-md absolute left-1/2 -translate-y-10 opacity-0 m-4 mx-auto whitespace-nowrap s-HuChg7XnXWfl"
            >
              Insert emoji
            </span>
          {/if}
        </div>

        {#if allowGiphy}
          <div class="flow-root group relative">
            <Giphy onimage={(detail) => insertGiphy(detail)} />
            <span
              class="group-hover:opacity-100 transition-opacity bg-gray-800 px-1 text-sm text-gray-100 rounded-md absolute left-1/2 -translate-y-10 opacity-0 m-4 mx-auto whitespace-nowrap s-HuChg7XnXWfl"
            >
              Insert gif image
            </span>
          </div>
        {/if}

        {#if uploadingImage}
          <div class="text-gray-400 flex gap-1">
            <LoaderCircle class="size-6 animate-spin" />
            <span>Uploading...</span>
          </div>
        {/if}

        {#if processingLink === "working"}
          <div class="text-gray-400 flex gap-1">
            <LoaderCircle class="size-6 animate-spin" />
            Converting link...
          </div>
        {/if}
      </div>
      <!-- End of footer -->

      <div class="flex mt-2 items-center gap-2">
        {#if uploadingFile}
          <div class="text-gray-400 flex">
            <Wave size="22" color="#9f9f9f" />&nbsp; Uploading...
          </div>
        {/if}

        {#if bottom}
          {@render bottom()}
        {/if}

        {#if showSaveArrow}
          {#if saving}
            <button
              class="inline-flex items-center rounded-md px-4 py-1 font-semibold text-white shadow-sm bg-green-800"
              data-name="tiptap-save-button"
              onclick={save}
            >
              <Loader class="size-5" />
            </button>
          {:else}
            <button
              data-name="tiptap-save-button"
              class="inline-flex items-center rounded-md px-4 py-1 font-semibold text-white shadow-sm hover:bg-green-600"
              class:bg-green-700={canSave}
              class:bg-gray-800={!canSave}
              onclick={save}
            >
              <SendHorizontal class="size-5" />
            </button>
          {/if}
        {/if}
      </div>
    </div>

    {#if allowFileUploads && files.length > 0}
      <ul class="list border-t p-4 border-gray-100">
        {#each files as file}
          <TiptapFile {file} {ondeletefile} {oninsertimage} />
        {/each}
      </ul>
    {/if}
  </div>
</div>
